import { PropsWithChildren, useEffect, useRef } from 'react';
import { useRootSelector } from '@/hooks/selector.hook';
import {
  prependNotificationAction,
} from '@/redux/slicers/notification.slice';
import { realtimeNotificationService } from '@/services/realtime-notification.service';
import { useNotificationService } from '@/services/notification.service';
import {
  normalizeNotification,
  normalizeNotificationChangedPayload,
} from '@/utils/notification';
import { useDispatch } from 'react-redux';

export const NotificationRealtimeProvider = ({ children }: PropsWithChildren) => {
  const dispatch = useDispatch();
  const { getNotifications, getNotificationStatistics } = useNotificationService();
  const { isAuthenticated, token, user } = useRootSelector((state) => state.auth);
  const userId = user?.id ?? user?.userId ?? '';
  const previousTokenRef = useRef<string | null | undefined>(token);

  useEffect(() => {
    if (!isAuthenticated || !token || !userId) {
      realtimeNotificationService.stop().catch((error) => {
        console.error('Failed to stop notification realtime:', error);
      });
      previousTokenRef.current = token;
      return;
    }

    const syncInitialNotifications = async () => {
      await Promise.all([getNotifications(), getNotificationStatistics()]);
    };

    realtimeNotificationService.setNotificationHandlers({
      onCommentNotificationChanged: (payload) => {
        const notification = normalizeNotification(payload);
        dispatch(prependNotificationAction(notification));
      },
      onNotificationChanged: (payload) => {
        const notification = normalizeNotificationChangedPayload(payload);
        if (!notification || notification.isRead) return;
        dispatch(prependNotificationAction(notification));
      },
    });

    syncInitialNotifications().catch((error) => {
      console.error('Failed to sync initial notifications:', error);
    });

    const starter =
      previousTokenRef.current && previousTokenRef.current !== token
        ? realtimeNotificationService.restart(userId)
        : realtimeNotificationService.start(userId);

    starter.catch((error) => {
      console.error('Failed to start notification realtime:', error);
    });

    previousTokenRef.current = token;

    return () => {
      realtimeNotificationService.stop().catch((error) => {
        console.error('Failed to stop notification realtime:', error);
      });
    };
  }, [dispatch, getNotifications, getNotificationStatistics, isAuthenticated, token, userId]);

  return children;
};
